import {MediaUpload} from '@wordpress/block-editor';
import {useEffect} from '@wordpress/element';
import {Button, TextControl, TextareaControl} from '@wordpress/components';
import {__} from '@wordpress/i18n';
import {DragDropContext, Droppable, Draggable} from '@hello-pangea/dnd';
import './style.css';

import 'swiper/swiper-bundle.css';

wp.blocks.registerBlockType('blank/slideshow', {
    title: __('Blank Slideshow'),
    icon: 'book-alt',
    category: 'design',
    keywords: [__('image'), __('slideshow'), __('video')],
    attributes: {
        slides: {
            type: 'array',
            default: [],
        },
        title: {
            type: 'string',
            default: '',
        },
        date: {
            type: 'string',
            default: '',
        },
        description: {
            type: 'string',
            default: '',
        },
        fallbackPoster: {
            type: 'string',
            default: ''
        }
    },

    edit({attributes, setAttributes}) {
        const {slides, title, date, description, fallbackPoster} = attributes;

        const addSlide = (newSlide) => {
            setAttributes({
                slides: [...slides, newSlide],
            });
        };

        const updateSlide = (index, updatedFields) => {
            const newSlides = [...slides];
            newSlides[index] = {
                ...newSlides[index],
                ...updatedFields,
            };
            setAttributes({slides: newSlides});
        };

        const removeSlide = (index) => {
            setAttributes({
                slides: slides.filter((_, i) => i !== index),
            });
        };

        useEffect(() => {
            if (!fallbackPoster) {
                const attachment = wp.media.attachment(140);
                attachment.fetch().then(() => {
                    const url = attachment.get('url');
                    setAttributes({fallbackPoster: url});
                });
            }
        }, []);

        return (
            <div className="blank-slideshow">
                <TextControl label={__('Title')}
                             value={title}
                             onChange={(value) => setAttributes({title: value})}/>
                <TextControl label={__('Date')}
                             value={date}
                             onChange={(value) => setAttributes({date: value})}/>
                <TextareaControl label={__('Description')}
                                 value={description}
                                 onChange={(value) => setAttributes({description: value})}/>

                <DragDropContext
                    onDragEnd={(result) => {
                        if (!result.destination) return;
                        const reordered = Array.from(slides);
                        const [moved] = reordered.splice(result.source.index, 1);
                        reordered.splice(result.destination.index, 0, moved);
                        setAttributes({slides: reordered});
                    }}
                >
                    <Droppable droppableId="slides-droppable">
                        {(provided) => (
                            <div{...provided.droppableProps}
                                ref={provided.innerRef}
                                className="slides">
                                {slides.map((slide, index) => (
                                    <Draggable key={index}
                                               draggableId={`slide-${index}`}
                                               index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef}
                                                 {...provided.draggableProps}
                                                 {...provided.dragHandleProps}
                                                 className="slide">
                                                {slide.url && (
                                                    <div className="slide-preview">
                                                        {slide.type === 'image' ? (
                                                            <img src={slide.url}
                                                                 alt={`Slide ${index}`}/>
                                                        ) : (
                                                            <video controls
                                                                   poster={slide.poster || fallbackPoster}>
                                                                <source src={slide.url}
                                                                        type="video/mp4"/>
                                                            </video>
                                                        )}
                                                        <Button variant="secondary"
                                                                onClick={() => removeSlide(index)}>
                                                            {__('Remove')}
                                                        </Button>


                                                        {slide.type === 'video' && (
                                                            <div className="poster-upload">
                                                                <MediaUpload onSelect={(media) => {
                                                                    updateSlide(index, {
                                                                        poster: media,
                                                                    });
                                                                }}
                                                                             allowedTypes={['image']}
                                                                             render={({open}) => (
                                                                                 <Button onClick={open}
                                                                                         variant="secondary">
                                                                                     {slide.poster ? __('Change Poster') : __('Add Poster')}
                                                                                 </Button>
                                                                             )}
                                                                />
                                                            </div>
                                                        )}


                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}

                                <MediaUpload onSelect={(media) => {
                                    const newSlide = {
                                        type: media.type,
                                        url: media.url,
                                        alt: media.alt,
                                        sizes: media.sizes || {},
                                    };
                                    addSlide(newSlide);
                                }}
                                             allowedTypes={['image', 'video']}
                                             render={({open}) => (
                                                 <Button variant="primary" onClick={open}>
                                                     {__('Add Slide')}
                                                 </Button>
                                             )}
                                />
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        );
    },

    save({attributes}) {
        const {slides, title, date, description, fallbackPoster} = attributes;

        const formattedDescription = description.split('\n').map((line, index) => (
            <>
                {line}
                <br/>
            </>
        ));

        return (
            <div>
                <div className="swiper swiper-main">
                    <div className="swiper-wrapper">
                        {slides.map((slide, index) => (
                            <div key={index} className="swiper-slide">
                                {slide.type === 'image' ? (
                                    <img src={slide.sizes?.large?.url || slide.url}
                                         {...(slide.sizes?.thumbnail?.x2 && {
                                             srcSet: `${slide.sizes.large.url} 1x, ${slide.sizes.large.x2} 2x`
                                         })}
                                         sizes="(max-width: 768px) 100vw, 1024px"
                                         alt={slide.alt || `Slide ${index}`}/>
                                ) : (
                                    <video autoPlay playsInline loop muted poster={slide.poster || fallbackPoster}>
                                        <source src={slide.url}
                                                type="video/mp4"/>
                                    </video>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="wp-block-blank-slideshow--info">
                    <div className="swiper swiper-thumbs">
                        <div className="swiper-wrapper">
                            {slides.map((slide, index) => (
                                <div key={index} className="swiper-slide">
                                    {slide.type === 'image' ? (
                                        <img src={slide.sizes?.thumbnail?.url || slide.url}
                                             {...(slide.sizes?.thumbnail?.x2 && {
                                                 srcSet: `${slide.sizes.thumbnail.url} 1x, ${slide.sizes.thumbnail.x2} 2x`
                                             })}
                                             sizes="30px"
                                             alt={slide.alt || `Slide ${index}`}/>
                                    ) : (
                                        <img
                                            src={slide.poster?.sizes?.thumbnail?.url || slide.poster?.url || fallbackPoster}
                                            {...(slide.poster?.sizes?.thumbnail?.x2 && {
                                                srcSet: `${slide.poster.sizes.thumbnail.url} 1x, ${slide.poster.sizes.thumbnail.x2} 2x`
                                            })}
                                            sizes="30px"
                                            alt={slide.alt || `Slide ${index}`}/>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <details>
                        <summary><span className={'date'}>[ {date} ]</span> {title}</summary>
                        <div className="content">{formattedDescription}</div>
                    </details>
                </div>
            </div>
        );
    }
})

